import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import {
  Clock,
  MapPin,
  Star,
  ArrowRight,
  Globe2,
  ChevronRight,
  Send,
  CheckCircle2,
} from "lucide-react";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import service, { getImageUrl } from "../../Service/Service";
import DestinationPicker from "../../components/Packages/DestinationPicker";
import { getPackageDetailPath } from "../../utils/packagePaths";
import "./PackagesPage.css";
import PriceDisplay from "../../components/PriceDisplay";

const emptyInquiryForm = () => ({
  country: "",
  name: "",
  phone: "",
  email: "",
});

const PACKAGE_IMAGE_KEYS = [
  "makkah_img1",
  "makkah_img2",
  "makkah_img3",
  "makkah_img4",
  "madina_img1",
  "madina_img2",
  "madina_img3",
  "madina_img4",
];

function getPackageImages(pkg) {
  return PACKAGE_IMAGE_KEYS.map((key) => pkg[key])
    .filter(Boolean)
    .map((raw) => getImageUrl(raw));
}

function PackageImageCarousel({ pkg, badgeLabel, nights }) {
  const images = getPackageImages(pkg);

  return (
    <div className="packages-card-image-wrap">
      {images.length > 1 ? (
        <Swiper
          modules={[Pagination]}
          pagination={{ clickable: true }}
          className="packages-card-swiper"
          touchStartPreventDefault={false}
          simulateTouch
          grabCursor
        >
          {images.map((url, index) => (
            <SwiperSlide key={`${pkg.uid}-${index}`}>
              <img
                src={url}
                alt={`${pkg.name} ${index + 1}`}
                className="packages-card-image"
                draggable={false}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : images.length === 1 ? (
        <img src={images[0]} alt={pkg.name} className="packages-card-image" />
      ) : (
        <div className="packages-card-image-fallback">
          <Globe2 className="packages-card-image-fallback-icon" />
        </div>
      )}

      <div className="packages-card-image-overlay" />

      {badgeLabel && (
        <div className="packages-card-badge-left">
          <span>{badgeLabel}</span>
        </div>
      )}

      {nights > 0 && (
        <div className="packages-card-badge-right">
          <Clock className="packages-card-badge-icon" />
          {nights} nights
        </div>
      )}
    </div>
  );
}

function PackageCard({ pkg, featured = false }) {
  const navigate = useNavigate();
  const nights = (pkg.makkah_nights || 0) + (pkg.madina_nights || 0);
  const starRating = [3, 4, 5].includes(Number(pkg.ratiing)) ? Number(pkg.ratiing) : null;
  const badgeLabel = starRating ? `${starRating} Star` : pkg.category_name;
  const detailPath = getPackageDetailPath(pkg);

  const openDetail = () => navigate(detailPath);

  return (
    <article
      className={`packages-card packages-card--clickable ${featured ? "packages-card--featured" : ""}`}
      onClick={openDetail}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openDetail();
        }
      }}
      role="link"
      tabIndex={0}
      aria-label={`View ${pkg.name} package details`}
    >
      <PackageImageCarousel pkg={pkg} badgeLabel={badgeLabel} nights={nights} />

      <div className="packages-card-body">
        {starRating > 0 && (
          <div className="packages-card-stars">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={
                  i < starRating
                    ? "packages-star packages-star--filled"
                    : "packages-star"
                }
              />
            ))}
            <span className="packages-card-rating">{starRating} Star</span>
          </div>
        )}

        <h3 className="packages-card-title">{pkg.name}</h3>

        {(pkg.dep_name || pkg.arrival_name) && (
          <p className="packages-card-route">
            <MapPin className="packages-card-route-icon" />
            {[pkg.dep_name, pkg.arrival_name].filter(Boolean).join(" → ")}
          </p>
        )}

        <div className="packages-card-divider" />

        <div className="packages-card-footer">
          <div>
            <p className="packages-card-from">from</p>
            <div className="packages-card-price-row">
              <span className="packages-card-price">
                <PriceDisplay amount={Number(pkg.price || 0)} />
              </span>
              <span className="packages-card-per">/ person</span>
            </div>
          </div>

          <div className="packages-card-cta">
            View Deal
            <ArrowRight className="packages-card-cta-icon" />
          </div>
        </div>
      </div>
    </article>
  );
}

function PackageSkeleton() {
  return (
    <div className="packages-skeleton">
      <div className="packages-skeleton-image" />
      <div className="packages-skeleton-body">
        <div className="packages-skeleton-line packages-skeleton-line--sm" />
        <div className="packages-skeleton-line packages-skeleton-line--lg" />
        <div className="packages-skeleton-line packages-skeleton-line--md" />
        <div className="packages-skeleton-divider" />
        <div className="packages-skeleton-footer">
          <div>
            <div className="packages-skeleton-line packages-skeleton-line--xs" />
            <div className="packages-skeleton-line packages-skeleton-line--price" />
          </div>
          <div className="packages-skeleton-line packages-skeleton-line--btn" />
        </div>
      </div>
    </div>
  );
}

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [inquiryForm, setInquiryForm] = useState(emptyInquiryForm());
  const [inquiryStatus, setInquiryStatus] = useState("idle");
  const [inquiryError, setInquiryError] = useState("");

  useEffect(() => {
    service
      .getAllpackageUmrah()
      .then((res) => {
        const data = res.data || [];
        const visible = data.filter(
          (p) => p.show_as_page === true || p.show_as_page === 1 || p.show_as_page === "1"
        );
        const list = visible.length > 0 ? visible : data;
        setPackages(list);
        setFiltered(list);
      })
      .catch(() => {
        setPackages([]);
        setFiltered([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const categories = [
    "all",
    ...Array.from(
      new Set(
        packages
          .map((p) => {
            const rating = Number(p.ratiing);
            if ([3, 4, 5].includes(rating)) return String(rating);
            return p.category_name || null;
          })
          .filter(Boolean)
      )
    ),
  ];

  useEffect(() => {
    let result = packages;
    if (activeCategory !== "all") {
      if (["3", "4", "5"].includes(activeCategory)) {
        result = result.filter((p) => Number(p.ratiing) === Number(activeCategory));
      } else {
        result = result.filter((p) => p.category_name === activeCategory);
      }
    }
    setFiltered(result);
  }, [activeCategory, packages]);

  const handleInquiryChange = (e) => {
    const { name, value } = e.target;
    setInquiryForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDestinationChange = (country) => {
    setInquiryForm((prev) => ({ ...prev, country }));
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setInquiryStatus("submitting");
    setInquiryError("");

    try {
      await service.addContact({
        name: inquiryForm.name.trim(),
        email: inquiryForm.email.trim(),
        phone: inquiryForm.phone.trim(),
        subject: `Packages enquiry - ${inquiryForm.country}`,
        message: `Country: ${inquiryForm.country}\nSubmitted from Packages page.`,
      });
      setInquiryStatus("success");
      setInquiryForm(emptyInquiryForm());
    } catch {
      setInquiryStatus("error");
      setInquiryError("Something went wrong. Please try again or contact us directly.");
    }
  };

  return (
    <div className="packages-page">
      <Navbar bg="blue" />

      <div className="packages-hero">
        <div className="packages-hero-inner">
          <p className="packages-hero-eyebrow">Curated for You</p>
          <h1 className="packages-hero-title">Travel Packages</h1>
          <p className="packages-hero-subtitle">
            Handpicked holiday and Umrah packages — flights, hotels, and transfers all included.
          </p>

          <div className="packages-inquiry-form-wrap">
            {inquiryStatus === "success" ? (
              <div className="packages-inquiry-success">
                <CheckCircle2 className="packages-inquiry-success-icon" />
                <p className="packages-inquiry-success-title">Thank you!</p>
                <p className="packages-inquiry-success-text">
                  Our travel experts will contact you shortly about packages for your chosen destination.
                </p>
                <button
                  type="button"
                  className="packages-inquiry-submit"
                  onClick={() => setInquiryStatus("idle")}
                >
                  Submit another enquiry
                </button>
              </div>
            ) : (
              <form className="packages-inquiry-form" onSubmit={handleInquirySubmit}>
                <div className="packages-inquiry-grid">
                  <div className="packages-inquiry-field packages-inquiry-field--full">
                    <label id="packages-country-label" htmlFor="packages-country" className="packages-inquiry-label">
                      Destination
                    </label>
                    <DestinationPicker
                      id="packages-country"
                      name="country"
                      value={inquiryForm.country}
                      onChange={handleDestinationChange}
                      required
                    />
                  </div>

                  <div className="packages-inquiry-field">
                    <label htmlFor="packages-name" className="packages-inquiry-label">
                      Name
                    </label>
                    <input
                      id="packages-name"
                      name="name"
                      type="text"
                      value={inquiryForm.name}
                      onChange={handleInquiryChange}
                      className="packages-inquiry-input"
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div className="packages-inquiry-field">
                    <label htmlFor="packages-phone" className="packages-inquiry-label">
                      Phone Number
                    </label>
                    <input
                      id="packages-phone"
                      name="phone"
                      type="tel"
                      value={inquiryForm.phone}
                      onChange={handleInquiryChange}
                      className="packages-inquiry-input"
                      placeholder="Your phone number"
                      required
                    />
                  </div>

                  <div className="packages-inquiry-field packages-inquiry-field--full">
                    <label htmlFor="packages-email" className="packages-inquiry-label">
                      Email
                    </label>
                    <input
                      id="packages-email"
                      name="email"
                      type="email"
                      value={inquiryForm.email}
                      onChange={handleInquiryChange}
                      className="packages-inquiry-input"
                      placeholder="you@example.com"
                      required
                    />
                  </div>

                  <div className="packages-inquiry-footer">
                    {inquiryStatus === "error" && (
                      <p className="packages-inquiry-error">{inquiryError}</p>
                    )}

                    <button
                      type="submit"
                      className="packages-inquiry-submit"
                      disabled={inquiryStatus === "submitting"}
                    >
                      {inquiryStatus === "submitting" ? (
                        "Sending..."
                      ) : (
                        <>
                          Get Package Quote
                          <Send className="packages-inquiry-submit-icon" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="packages-content">
        {!loading && categories.length > 1 && (
          <div className="packages-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`packages-filter-btn ${
                  activeCategory === cat ? "packages-filter-btn--active" : ""
                }`}
              >
                {cat === "all"
                  ? "All Packages"
                  : ["3", "4", "5"].includes(cat)
                    ? `${cat} Star`
                    : cat}
              </button>
            ))}
          </div>
        )}

        {!loading && (
          <div className="packages-count-row">
            <p className="packages-count">
              <span className="packages-count-num">{filtered.length}</span> package
              {filtered.length !== 1 ? "s" : ""} available
            </p>
          </div>
        )}

        {loading ? (
          <div className="packages-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <PackageSkeleton key={i} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="packages-empty">
            <div className="packages-empty-icon-wrap">
              <MapPin className="packages-empty-icon" />
            </div>
            <p className="packages-empty-title">No packages found.</p>
            <p className="packages-empty-subtitle">Try a different category filter.</p>
          </div>
        ) : (
          <div className="packages-grid">
            {filtered.map((pkg, idx) => (
              <PackageCard
                key={pkg.uid}
                pkg={pkg}
                featured={idx === 0 && filtered.length >= 3}
              />
            ))}
          </div>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <div className="packages-cta-band">
          <div className="packages-cta-inner">
            <h2 className="packages-cta-title">Can&apos;t find what you&apos;re looking for?</h2>
            <p className="packages-cta-subtitle">
              Our travel experts will craft a bespoke itinerary just for you.
            </p>
            <Link to="/contact" className="packages-cta-btn">
              Speak to an Expert <ChevronRight className="packages-cta-btn-icon" />
            </Link>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Packages;
